import { Box, EmptyState, Spinner, Text, Button } from "@shopify/polaris";
import type { BlockSchema } from "@/types/sections";
import { XIcon } from "@shopify/polaris-icons";
import { lazy, Suspense } from "react";
import { useSelectionStore } from "@/state/selectionStore";
import { useSectionSchemas } from "@/queries/sections";
import { useStagedDraft } from "@/queries/stagedDraft";

const ColorsForm = lazy(() =>
  import("@/design/ColorsForm").then((m) => ({ default: m.ColorsForm })),
);
const TypographyForm = lazy(() =>
  import("@/design/TypographyForm").then((m) => ({
    default: m.TypographyForm,
  })),
);
const AppearanceForm = lazy(() =>
  import("@/design/AppearanceForm").then((m) => ({
    default: m.AppearanceForm,
  })),
);
const SearchBarForm = lazy(() =>
  import("@/design/SearchBarForm").then((m) => ({ default: m.SearchBarForm })),
);
const ProductCardForm = lazy(() =>
  import("@/design/ProductCardForm").then((m) => ({
    default: m.ProductCardForm,
  })),
);
const NavForm = lazy(() =>
  import("@/design/NavForm").then((m) => ({ default: m.NavForm })),
);
const LabelsForm = lazy(() =>
  import("@/design/LabelsForm").then((m) => ({ default: m.LabelsForm })),
);
const ShopTabForm = lazy(() =>
  import("@/design/ShopTabForm").then((m) => ({ default: m.ShopTabForm })),
);
const AnimationsForm = lazy(() =>
  import("@/design/AnimationsForm").then((m) => ({
    default: m.AnimationsForm,
  })),
);
const CheckoutForm = lazy(() =>
  import("@/design/CheckoutForm").then((m) => ({ default: m.CheckoutForm })),
);
const OrdersForm = lazy(() =>
  import("@/design/OrdersForm").then((m) => ({ default: m.OrdersForm })),
);
const RestockForm = lazy(() =>
  import("@/design/RestockForm").then((m) => ({ default: m.RestockForm })),
);
const ContactUsForm = lazy(() =>
  import("@/design/ContactUsForm").then((m) => ({ default: m.ContactUsForm })),
);
const AppGateForm = lazy(() =>
  import("@/design/AppGateForm").then((m) => ({ default: m.AppGateForm })),
);
const AdvancedForm = lazy(() =>
  import("@/design/AdvancedForm").then((m) => ({ default: m.AdvancedForm })),
);
const SectionSettingsPanel = lazy(() =>
  import("@/sections/SectionSettingsPanel").then((m) => ({
    default: m.SectionSettingsPanel,
  })),
);
const BlockSettingsPanel = lazy(() =>
  import("@/sections/BlockSettingsPanel").then((m) => ({
    default: m.BlockSettingsPanel,
  })),
);

function PanelFallback() {
  return (
    <div style={{ display: "grid", placeItems: "center", padding: 32 }}>
      <Spinner accessibilityLabel="Loading panel" size="small" />
    </div>
  );
}

function humanize(type: string): string {
  return type.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function PanelHeader({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose?: () => void;
}) {
  return (
    <div
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid var(--p-color-border)",
        background: "var(--p-color-bg-surface)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Text as="h2" variant="headingMd" truncate>
          {title}
        </Text>
        {subtitle && (
          <Text as="p" tone="subdued" variant="bodySm">
            {subtitle}
          </Text>
        )}
      </div>
      {onClose && (
        <Button
          icon={XIcon}
          variant="tertiary"
          onClick={onClose}
          accessibilityLabel="Close"
        />
      )}
    </div>
  );
}

export function SettingsPanel() {
  const viewMode = useSelectionStore((s) => s.viewMode);
  const themeTab = useSelectionStore((s) => s.themeTab);
  const selection = useSelectionStore((s) => s.selection);
  const clearSelection = useSelectionStore((s) => s.clearSelection);
  const { template: tmpl } = useStagedDraft();
  const schemas = useSectionSchemas();

  let title = "Settings";
  let subtitle: string | undefined;
  let body: React.ReactNode;
  let onClose: (() => void) | undefined;

  if (viewMode === "theme-settings") {
    if (themeTab === "colors") {
      title = "Colors";
      subtitle = "Global color palette";
      body = <ColorsForm />;
    } else if (themeTab === "typography") {
      title = "Typography";
      subtitle = "Fonts, sizes, weights";
      body = <TypographyForm />;
    } else if (themeTab === "searchBar") {
      title = "Search bar";
      subtitle = "Search pill background";
      body = <SearchBarForm />;
    } else if (themeTab === "productCard") {
      title = "Product card";
      subtitle = "Card display, image, spacing";
      body = <ProductCardForm />;
    } else if (themeTab === "nav") {
      title = "Navigation";
      subtitle = "Position, size, nav items";
      body = <NavForm />;
    } else if (themeTab === "labels") {
      title = "Product labels";
      subtitle = "Badges, discount label";
      body = <LabelsForm />;
    } else if (themeTab === "shopTab") {
      title = "Shop tab";
      subtitle = "Which Shopify menu drives it";
      body = <ShopTabForm />;
    } else if (themeTab === "animations") {
      title = "Animations";
      subtitle = "Skeleton, sticky ATC, spring, parallax";
      body = <AnimationsForm />;
    } else if (themeTab === "checkout") {
      title = "Checkout";
      subtitle = "Native checkout colors, text, toggles";
      body = <CheckoutForm />;
    } else if (themeTab === "orders") {
      title = "Order screens";
      subtitle = "Thumbnails, cancel button, restock toggles";
      body = <OrdersForm />;
    } else if (themeTab === "restock") {
      title = "Back-in-stock";
      subtitle = "Push + email notifications when items restock";
      body = <RestockForm />;
    } else if (themeTab === "contactUs") {
      title = "Contact Us";
      subtitle = "Email, WhatsApp, phone, location, hours, social";
      body = <ContactUsForm />;
    } else if (themeTab === "appGate") {
      title = "App availability";
      subtitle = "Close the app with a notice, or force an update";
      body = <AppGateForm />;
    } else if (themeTab === "advanced") {
      title = "Advanced";
      subtitle = "Theme JSON (read-only)";
      body = <AdvancedForm />;
    } else {
      title = "Appearance";
      subtitle = "Corner radius, shadows, icons";
      body = <AppearanceForm />;
    }
  } else if (selection.sectionId && selection.blockId) {
    const section = tmpl?.sections[selection.sectionId];
    const block = section?.blocks?.[selection.blockId];
    const blockSchema = section
      ? schemas.data?.[section.type]?.blocks?.find(
          (b: BlockSchema) => b.type === block?.type,
        )
      : undefined;
    title = block
      ? (block.settings.title as string) ||
        (block.settings.heading as string) ||
        blockSchema?.name ||
        humanize(block.type)
      : "Block";
    subtitle = block ? `Block · ${block.type}` : undefined;
    body = (
      <BlockSettingsPanel
        sectionId={selection.sectionId}
        blockId={selection.blockId}
      />
    );
    onClose = clearSelection;
  } else if (selection.sectionId) {
    const section = tmpl?.sections[selection.sectionId];
    const schema = section ? schemas.data?.[section.type] : undefined;
    title = section
      ? (section.settings.title as string) ||
        schema?.name ||
        humanize(section.type)
      : "Section";
    subtitle = section ? `Section · ${section.type}` : undefined;
    body = <SectionSettingsPanel sectionId={selection.sectionId} />;
    onClose = clearSelection;
  } else {
    title = "No selection";
    body = (
      <Box padding="600">
        <EmptyState heading="Pick a section to edit" image="">
          <p>
            Click a section in the tree on the left to see its settings here.
          </p>
        </EmptyState>
      </Box>
    );
  }

  return (
    <aside
      style={{
        width: 360,
        flexShrink: 0,
        borderLeft: "1px solid var(--p-color-border)",
        background: "var(--p-color-bg-surface)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <PanelHeader title={title} subtitle={subtitle} onClose={onClose} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <Suspense fallback={<PanelFallback />}>{body}</Suspense>
      </div>
    </aside>
  );
}
